import type { LiteralUnion, SetDefined, ValueUnion } from '@eturi/ts-helpers'
import type { PairStatus } from './PairStatus.js'

export type DeviceOS = 'Android' | 'iOS' | 'Unknown'

export type WithDeviceOS<T extends DeviceOS> = {
	readonly os: T
}

export type DeviceType = ValueUnion<typeof DeviceType>
export const DeviceType = {
	Account: 1,
	Managed: 10,
} as const

export type IOS_SUPERVISED = 'ios-supervised'

export type ChildAppBundle = ValueUnion<typeof ChildAppBundle>
export const ChildAppBundle = {
	ANDROID: 'com.eturi.ourpactjr',
	IOS: 'com.parentsware.ourpact.childapp',
} as const

export type RawDevice = {
	readonly account_id: string
	readonly app: Maybe<string>
	readonly app_version: Maybe<string>
	// readonly busy_count: null
	// readonly cert_issuer: null
	// readonly channels: null
	readonly attributes: Maybe<DeviceAttributes>
	readonly command_state: Maybe<string>
	readonly command_state_ts: Maybe<number>
	readonly created: number
	// readonly current_command_def_ts: null
	readonly current_command_id: Maybe<string>
	readonly current_command_ts: Maybe<number>
	readonly delivered_command_id: Maybe<string>
	readonly delivered_command_ts: Maybe<number>
	readonly device_id: string
	readonly device_location_active: Maybe<boolean>
	readonly device_location_ts: Maybe<number>
	readonly display_name: Maybe<string>
	readonly display_state: Maybe<string>
	readonly error_count: Maybe<number>
	readonly fcm_token: Maybe<string>
	// readonly imei: Maybe<string>
	// readonly installation_id: Maybe<string>
	readonly last_activity: Maybe<number>
	// readonly last_scheduled_id: Maybe<string>
	readonly last_seen: Maybe<number>
	readonly make: Maybe<string>
	readonly management_level: Maybe<IOS_SUPERVISED>
	readonly management_removed: Maybe<boolean>
	// readonly mdm_organization: Maybe<string>
	// readonly mdm_token: Maybe<string>
	// readonly meid: Maybe<string>
	readonly model: Maybe<string>
	readonly model_name: Maybe<string>
	readonly notification_count: Maybe<number>
	readonly notification_ts: Maybe<number>
	// Suppressing eslint, b/c the union is not for type enforcement, but is
	// instead informational. This will usually be 'android', 'iOS', '', or null
	//eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
	readonly os: Maybe<LiteralUnion<DeviceOS, string>>
	readonly os_version: Maybe<string>
	readonly pair_id: Maybe<string>
	readonly retired: Maybe<number>
	readonly schedule_ts: Maybe<number>
	// readonly serial: null
	readonly suspended: Maybe<boolean>
	readonly system_components: Maybe<ChildAppBundle>
	readonly time_zone: Maybe<string>
	// readonly topic: Maybe<string>
	readonly type: DeviceType
	readonly udid: Maybe<string>
	// readonly unlock_token: null
	readonly users: Maybe<string[]>
	readonly version: Maybe<string>
}

export type Device = SetDefined<
	RawDevice,
	| 'app_version'
	| 'device_location_active'
	| 'error_count'
	| 'make'
	| 'management_removed'
	| 'model'
	| 'os'
	| 'suspended'
	| 'users'
> & {
	readonly displayName: string
	readonly pairStatus: PairStatus
}

export type NewAccountDevice = {
	readonly app: string
	readonly app_version: string
	readonly fcm_token?: Maybe<string>
	readonly make: string
	readonly model: string
	readonly os: string
	readonly os_version: string
	readonly time_zone: string
	readonly type: DeviceType
	readonly udid?: string
	readonly users?: string[]
	readonly version: string
}

export type LoginDevice = NewAccountDevice & { readonly device_id?: string }

export type DeviceAttributes = {
	readonly battery_level?: number
	readonly battery_level_ts?: number
}

/**
 * This is just a type helper that ensures the second argument conforms to `RawDevice` partial.
 * It's wrapped in `Readonly` so we can omit the `readonly` prefix on the fields.
 */
export type WithDeviceFields<T, U extends Partial<RawDevice>> = Readonly<T & U>

export type DeviceAndroid<T extends Pick<RawDevice, 'os'>> = WithDeviceFields<T, { os: 'android' }>
export type DeviceIOS<T extends Pick<RawDevice, 'os'>> = WithDeviceFields<T, { os: 'iOS' }>

export type DeviceIOSSupervised<T extends RawDevice> = WithDeviceFields<
	T,
	{ management_level: 'ios-supervised' }
>

export type DeviceTypeAccount<T extends RawDevice> = WithDeviceFields<
	T,
	{ type: typeof DeviceType.Account }
>

export type DeviceTypeManaged<T extends RawDevice> = WithDeviceFields<
	T,
	{ os: DeviceOS; type: typeof DeviceType.Managed }
>

export type DeviceWithIOSChildApp<T extends RawDevice> = WithDeviceFields<
	T,
	{ system_components: typeof ChildAppBundle.IOS }
>

export type AccountDevice = DeviceTypeAccount<Device>
export type ManagedDevice = DeviceTypeManaged<Device>
export type RawAccountDevice = DeviceTypeAccount<RawDevice>
export type RawManagedDevice = DeviceTypeManaged<RawDevice>
